/**
 * ADSP Ads Manager view - root of the server-filtered, lazy-loaded CCG tree:
 * PRODUCT_SET (CCG) -> CONCEPT -> CREATIVE -> ASSET. This view owns the shared
 * Live/Removed status filter and the paginated top-level PRODUCT_SET query; each
 * deeper level fetches its own children on demand (see AdspProductSetCard).
 *
 * The status filter narrows CONCEPTs only. CCGs are listed unfiltered because a
 * CCG row's status says nothing about the concepts it holds: filtering it would
 * hide every concept beneath it whenever the two disagree (e.g. a SYNCED CCG
 * under the Failed filter). Creatives and assets are likewise shown in full
 * under a visible concept, each with its own status badge.
 *
 * ADSP has no CAMPAIGN/AD_GROUP/AD entities, so there is no Google-style entity
 * toggle. Children join to parents by the backend's parentSourceEntityId linkage:
 *   CONCEPT.parentSourceEntityId  === PRODUCT_SET.publisherEntityId  (the CCG id)
 *   CREATIVE.parentSourceEntityId === CONCEPT.sourceEntityId
 *   ASSET.parentSourceEntityId    === CREATIVE.sourceEntityId
 */
import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import { useTranslation } from 'src/arb-wrapper';
import { Loader } from 'src/components/common/Loader';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { AdspPaginationFooter } from './AdspPaginationFooter';
import { AdspProductSetCard } from './AdspProductSetCard';
import { StatusFilterSelect } from './StatusFilterSelect';
import type { AdsStatusFilter } from './statusFilterParams';
import { useAdspEntityLevel } from './useAdspEntityLevel';

interface AdspEntitiesViewProps {
  campaignId: string;
  onRefresh?: () => void;
}

const PRODUCT_SET_SORT = ['createdAt:-1'];

export const AdspEntitiesView: React.FC<AdspEntitiesViewProps> = ({ campaignId, onRefresh }) => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<AdsStatusFilter>('LIVE');

  const { page, query, setPage } = useAdspEntityLevel({
    campaignId,
    publisherEntityType: 'PRODUCT_SET',
    sort: PRODUCT_SET_SORT,
  });

  const productSets = (query.data?.data ?? []) as PublisherEntityDto[];
  const pagination = query.data?.meta.pagination;

  return (
    <Loader isLoading={query.isLoading}>
      <Box spacingInset="500" type="outline">
        <Column spacing="400">
          <Row alignmentHorizontal="justify" alignmentVertical="center">
            <Text type="h200">{t('ads_manager_product_sets_header')}</Text>
            {onRefresh != null && (
              <Row alignmentVertical="center" spacing="300">
                {query.isFetching && <MeridianLoader size="small" type="linear" />}
                <Column alignmentHorizontal="center" spacing="100">
                  <Button
                    data-testid="ads-manager-refresh-button"
                    disabled={query.isFetching}
                    size="small"
                    type="icon"
                    onClick={onRefresh}
                  >
                    <Icon tokens={refreshTokens} />
                  </Button>
                  <Text color="secondary" type="b100">{t('ads_manager_refresh')}</Text>
                </Column>
              </Row>
            )}
          </Row>
          <Divider />

          <Row alignmentVertical="center" spacing="200">
            <Text type="b300">{t('ads_manager_filter_status')}</Text>
            <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
          </Row>

          {query.isError && (
            <Alert data-testid="ads-manager-entities-query-error" size="small" type="error">
              {t('ads_manager_query_error')}
            </Alert>
          )}

          {!query.isError && productSets.length === 0 && (
            <Text color="secondary" data-testid="ads-manager-no-product-sets">
              {t('ads_manager_no_product_sets')}
            </Text>
          )}

          {productSets.length > 0 && (
            <Column spacing="300">
              {productSets.map((productSet, index) => (
                <AdspProductSetCard
                  campaignId={campaignId}
                  defaultExpanded={index === 0}
                  key={productSet._id}
                  productSet={productSet}
                  statusFilter={statusFilter}
                />
              ))}
            </Column>
          )}

          {pagination != null && (
            <AdspPaginationFooter
              isFetching={query.isFetching}
              page={page}
              pagination={pagination}
              testId="ads-manager-product-sets"
              onPageChange={setPage}
            />
          )}
        </Column>
      </Box>
    </Loader>
  );
};
